<script setup>

import {computed, inject, onMounted} from 'vue'
import API from "@/classes/global/API";

import StickyGrid from "@/components/common/other/StickyGrid";
import Button from "@/components/common/input/Button";
import StaticWidget from "@/components/common/other/StaticWidget";
import MultiSearchDropDown from "@/components/common/input/MultiSearchDropDown";
import StaticTooltip from "@/components/common/other/StaticTooltip";
import DateInput from "@/components/common/input/DateInput";
import DropDownTextArea from "@/components/common/input/DropDownTextArea";
import SearchStockPillGroup from "@/components/warehouse/SearchStockPillGroup";
import GridCellDetail from "@/components/common/other/GridCellDetail";
import MoreInfoPopOutPanel from "@/components/common/other/MoreInfoPopOutPanel";
import StockDescriptions from "@/components/warehouse/StockDescriptions";
import Modal from "@/components/common/other/Modal.vue";
import AddressEntry from "@/components/common/other/AddressEntry.vue";
import ContactDetail from "@/classes/bookings/ContactDetail";
import ContactEntry from "@/components/common/other/ContactEntry.vue";
import Address from "@/classes/global/Address";
import PickStockConsignee from "@/classes/stock/PickStockConsignee";

import {useStore} from 'vuex';
import {useRoute} from "vue-router";
import {useMapAddressAndContacts} from "@/composables/common/useMapAddressAndContacts";
import {useClearDescriptionHeaders} from "@/composables/stock/useClearDescriptionHeaders";
import {useCheckSearchCriteriaExists} from "@/composables/common/useCheckSearchCriteriaExists";
import {useTimeGenerator} from "@/composables/common/useTimeGenerator";

const store = useStore();
const route = useRoute();
const pickStock = inject("pickStock");

const props = defineProps({

  modelValue: {}
})

const emits = defineEmits(['update-picked-items']);

const address = computed(() => {

  if (pickStock.value.openNewConsigneeModal) return pickStock.value.newConsigneeCompany;

  return pickStock.value.consignee;
})

const contact = computed(() => {

  if (pickStock.value.openAddContactModal) return pickStock.value.newConsigneeContact;

  return pickStock.value.consignee.selectedContact;
})

const handlePlaceholder = computed(() => {

  const checkSelected = ['customerRef', 'releaseDate', 'deliveryDate', 'action', 'deliveryInstructions'];

  let result = false;

  checkSelected.forEach((item) => {

    if (pickStock.value[item]) {
      result = true;

    }

  })

  return result


})


onMounted(() => {

  prepareMetaData();

})


async function prepareMetaData() {

  const response = await API.getStockMetaData(route, 'pickStockValidationFields');

  if (response) pickStock.value.metaData = response;

}

async function getCompanyDetails(name) {

  pickStock.value.consignee.companyName = name.value;

  let response = await API.getCompanyDetail(name.id, route)

  if (response) useMapAddressAndContacts(response, pickStock.value);

}

function handlePick(rowData) {

  emits('update-picked-items', rowData.pickQuantity)

  if (rowData.pickQuantity) {

    let isExistingRowData = props.modelValue.pickedItems.find((item) => {
      return rowData.skuComposite === item.skuComposite
    })

    if (isExistingRowData) return;

    props.modelValue.pickedItems.push(rowData);
    props.modelValue.openPicksDrawer = true;
    rowData.allocated = rowData.pickQuantity;
  }
}

function toggleSearchGroup(button) {

  if (button && pickStock.value.searchGroup) return;
  if (!button && !pickStock.value.searchGroup) return;

  pickStock.value.searchGroup = !pickStock.value.searchGroup;
}

async function getStockHeaders(selected) {

  useClearDescriptionHeaders(pickStock.value);

  const response = await API.getStockDescriptions(route, selected.id);

  if (response) {

    for (const each in response) {

      if (response[each]) {

        pickStock.value.skuGroupSelected = true;
        pickStock.value.searchPills[each].enabled = true;
        pickStock.value.searchPills[each].label = response[each];

      } else {

        pickStock.value.searchPills[each].enabled = false;

      }
    }
  }

  handlePickStockSearch();

}

async function clearHeaders() {

  useClearDescriptionHeaders(pickStock.value);

  if (useCheckSearchCriteriaExists(props.modelValue.searchCriteria)) {

    handlePickStockSearch();

  } else {

    pickStock.value.searchData = [];
  }

}

async function handlePickStockSearch() {

  const response = await API.searchStock(route, pickStock.value.searchCriteria, 'pickstock');

  pickStock.value.searchData = response;

}

function mapSelectedConsigneeContactDetail(contact) {

  pickStock.value.consignee.selectedContact = contact;
  // pickStock.value.consignee.contactId = contact.id;
  // pickStock.value.consignee.contactPhone = contact.phone;
  // pickStock.value.consignee.contactEmail = contact.email

}

function handleClearCompany() {

  pickStock.value.consignee = new PickStockConsignee();

}

function disableGridShowButton(skuProperties) {

  for (const each in skuProperties) {
    if (skuProperties[each].value) return true
  }

  return false
}

function handleClearData() {

  pickStock.value.searchData = [];
}

function addNewConsigneeCompany() {

  pickStock.value.openNewConsigneeModal = true;
}

function editConsigneeCompany() {

  pickStock.value.openEditConsigneeModal = true;
}

function saveNewConsigneeCompany() {

  pickStock.value.newConsigneeCompany.newCompanySaved = true;
}

function saveEditedConsigneeCompany(company) {

  console.log(company);
}

function editNewCompany() {

  pickStock.value.newConsigneeCompany.newCompanySaved = false;
}

function clearConsigneeContact() {

  pickStock.value.consignee.selectedContact = new ContactDetail();

}

function addNewConsigneeContact() {

  pickStock.value.openAddContactModal = true;
}

function editConsigneeContact() {

  pickStock.value.openEditContactModal = true;

}

function clearNewConsigneeForm() {

  pickStock.value.newConsigneeCompany = new Address();
}

</script>

<template class="cmp-pick-stock">
  <StaticWidget title="Delivery Information" height="230" background="#fcfcfc">
    <template #extra-content>
      <div style="margin-left: 80px; font-weight: normal">Warehouse Job Number:
        <span style="color: var(--aqua-blue-definition)">{{ pickStock.jobNumber }}</span>
        <Button
            style="margin-left: 20px;"
            type="toolbar"
            text="Cancel Job">
        </Button>
      </div>
    </template>
    <template #right-side>
      <Button
          type="warning"
          @click="props.modelValue.openDocsDrawer = true;"
          icon="<i class='fa-solid fa-folder-plus'></i>"
          text="Add/View Docs">
      </Button>
      <Button
          type="primary"
          icon="<i class='fa-light fa-floppy-disk'></i>"
          text="Save & Submit">
      </Button>
    </template>
    <div class="cmp-pick-stock_delivery-section-wrapper">
      <div class="cmp-pick-stock_delivery-section-layout">
        <div class="cmp-pick-stock_address-entry-section">
          <div class="cmp-pick-stock_field-button-layout">
            <MultiSearchDropDown
                :key="pickStock.metaData.consignees"
                :allowCustomValue="false"
                :useServerSearch="true"
                :maxLength="50"
                label="Consignee & Address"
                :label-top="true"
                :width="400"
                searchType="consignee"
                v-model="pickStock.consignee.companyName"
                @select="getCompanyDetails($event, 'consignee')"
                @clear="handleClearCompany()"
                :dataSource="pickStock.metaData.consignees"
                :selectPrimitiveValue="true">
            </MultiSearchDropDown>
            <div class="cmp-pick-stock_address-buttons">
              <i @click="addNewConsigneeCompany" class="fa-solid fa-circle-plus global-add-icon" title="Create New Consignee"
                 style="margin-right: 8px;"></i>
              <i v-if="pickStock.consignee.companyName" @click="editConsigneeCompany"
                 class="fa-solid fa-pen-to-square global-edit-icon" title="Edit This Consignee">
              </i>
            </div>
          </div>
          <div v-if="!pickStock.consignee.companyName" v-for="each in 4">
            <div class="cmp-pick-stock_address-placeholder"></div>
          </div>
          <div class="cmp-pick-stock_address-details">
            <div title="Address Line 1">{{ pickStock.consignee.addressLine1 }}</div>
            <div title="Address Line 2">{{ pickStock.consignee.addressLine2 }}</div>
            <div title="City">{{ pickStock.consignee.city }}</div>
            <div title="County">{{ pickStock.consignee.county }}</div>
            <div title="Post Code">{{ pickStock.consignee.postCode }}</div>
            <div title="Country">{{ pickStock.consignee.country }}</div>
          </div>
          <div style="position: absolute; top: 126px">
            <div class="cmp-pick-stock_field-button-layout">
              <MultiSearchDropDown
                  :key="pickStock.consignee.contactList"
                  :width="400"
                  :maxLength="50"
                  label="Contact"
                  style="margin-top: 20px;"
                  :label-top="true"
                  placeholder="Select..."
                  :allowCustomValue="true"
                  @select="mapSelectedConsigneeContactDetail"
                  @clear="clearConsigneeContact"
                  :disableFilter="true"
                  v-model="pickStock.consignee.selectedContact.value"
                  :data-source="pickStock.consignee.contactList"
                  :selectPrimitiveValue="true">
              </MultiSearchDropDown>
              <div style="margin-top: 20px;">
                <i v-if="pickStock.consignee.companyName"
                   @click="addNewConsigneeContact()"
                   class="fa-solid fa-circle-plus global-add-icon"
                   style="margin-right: 8px">
                </i>
                <i v-if="pickStock.consignee.selectedContact.value"
                   @click="editConsigneeContact()"
                   class="fa-solid fa-pen-to-square global-edit-icon">
                </i>
              </div>
            </div>
            <div v-if="!pickStock.consignee.selectedContact.value" v-for="each in 2">
              <div class="cmp-pick-stock_contact-placeholder"></div>
            </div>
            <div class="cmp-pick-stock_contact-details">
              <div class="cmp-pick-stock_contact-info">
                <div>
                  <i v-if="pickStock.consignee.selectedContact.value" class="fa-duotone fa-user-large cmp-pick-stock_contact-icons"></i>
                  {{ pickStock.consignee.selectedContact.value }}
                </div>
                <div>
                  <i v-if="pickStock.consignee.selectedContact.phone" class="fa-duotone fa-square-phone cmp-pick-stock_contact-icons"></i>
                  {{ pickStock.consignee.selectedContact.phone }}
                </div>
                <div>
                  <i v-if="pickStock.consignee.selectedContact.email" class="fa-duotone fa-envelope cmp-pick-stock_contact-icons"></i>
                  {{ pickStock.consignee.selectedContact.email }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="cmp-pick-stock_delivery-information-area">
          <TextInput
              v-model="pickStock.customerRef"
              :width="400"
              :maxLength="50"
              :labelTop="true"
              label="Customer Ref">
          </TextInput>
          <div class="cmp-pick-stock_release-date-area">
            <DateInput
                v-model="pickStock.releaseDate"
                style="margin-top: 20px"
                :labelWidth="118"
                :labelTop="true"
                label="Release Date">
              <el-date-picker
                  :clearable="false"
                  value-format="DD-MMM-YY"
                  v-model="pickStock.releaseDate"></el-date-picker>
            </DateInput>
            <MultiSearchDropDown
                v-model="pickStock.releaseTime"
                style="margin-top: 20px"
                :width="121"
                :labelWidth="75"
                :labelTop="true"
                :maxLength="50"
                placeholder="Select..."
                :allowCustomValue="false"
                :disableFilter="true"
                label="Release Time"
                :data-source="useTimeGenerator(24)">
              :selectPrimitiveValue="true">
            </MultiSearchDropDown>
          </div>
          <DateInput
              v-model="pickStock.deliveryDate"
              style="margin-top: 20px"
              :labelWidth="118"
              :labelTop="true"
              label="Delivery Date">
            <el-date-picker
                :clearable="false"
                value-format="DD-MMM-YY"
                v-model="pickStock.deliveryDate"></el-date-picker>
          </DateInput>
          <TextInput
              v-model="pickStock.action"
              :width="400"
              style="margin-top: 20px"
              :labelTop="true"
              :maxLength="50"
              label="Action">
          </TextInput>
          <DropDownTextArea
              v-model="pickStock.deliveryInstructions"
              style="margin-top: 20px"
              :labelTop="true"
              :width="400"
              label="Delivery Instructions">
          </DropDownTextArea>
        </div>
      </div>
    </div>
  </StaticWidget>
  <StaticWidget title="Search Stock" height="430">
    <template #extra-content>
      <div style=" margin-left: 80px; font-weight: normal">SKU Group</div>
      <div style="font-weight: normal" class="global-flex-layout">
        <MultiSearchDropDown
            :key="pickStock.metaData.skuGroups"
            :width="320"
            :labelWidth="85"
            :maxLength="50"
            placeholder="Select..."
            @select="getStockHeaders"
            @clear="clearHeaders"
            :allowCustomValue="false"
            :disableFilter="true"
            v-model="pickStock.searchCriteria.skuGroup"
            :dataSource="pickStock.metaData.skuGroups"
            :selectPrimitiveValue="true">
        </MultiSearchDropDown>
        <StaticTooltip :icon="false">
          <template #content>
            <div style="width: 340px;">
              <strong>Search</strong> by completing any field and press any search icon
              <span><i class="fa-duotone fa-square-arrow-right" style="color: #114362"></i></span> or press enter within
              the field.
              Any fields with data are considered when searching records, some criteria may return no results. Selecting
              a
              SKU Group will bring new search criteria where available. A search group toggle will appear to view other
              fields.
              Press Refresh to return the latest records for the current search, or reset the data to initial records.
            </div>
          </template>
          <i :class="store.state.globalHelpIcon"></i>
        </StaticTooltip>
        <div v-if="pickStock.skuGroupSelected" class="cmp-stock_search-group-selection">
          <span>Search Group</span>
          <Button
              @click="toggleSearchGroup(true)"
              type="toolbar"
              text="1"
              :class="{'cmp-stock_search-group-selection-icon-active' : pickStock.searchGroup}"
              style="width: 30px;">
          </Button>
          <Button
              @click="toggleSearchGroup(false)"
              type="toolbar"
              text="2"
              :class="{'cmp-stock_search-group-selection-icon-active' : !pickStock.searchGroup}"
              style="width: 30px;">
          </Button>
        </div>
      </div>
    </template>
    <template #right-side>
      <Button
          @click="props.modelValue.openPicksDrawer = !props.modelValue.openPicksDrawer"
          icon="<i class='fa-duotone fa-grid-2'></i>"
          type="info"
          :badgeData="pickStock.pickedItems.length"
          text="View Picks">
      </Button>
    </template>
    <SearchStockPillGroup
        url="pickstock"
        @clear="handleClearData"
        v-model="pickStock">
    </SearchStockPillGroup>
    <div style="margin-top: 10px;">
      <StickyGrid
          :key="pickStock.searchData.length"
          :allowGridMenu="false"
          :height="300"
          :width="98"
          :page-size="200"
          :selectable="false"
          :highlightRows="false"
          :allow-column-resize="false"
          :row-selection="false"
          v-model:currentPage="pickStock.searchResultCurrentPage"
          v-model:currentPaginationRange="pickStock.searchResultPaginationRange"
          :dataSource="pickStock.searchData"
          :columns="pickStock.searchGridColumns">
        <!--        <template v-slot:available="row">-->
        <!--          <div style="display: grid; grid-template-columns: 40px 100px;">-->
        <!--            {{row.rowData.available}}<span v-if="row.rowData.isLowStock" style="font-size: 11px; color: #b04545">-->
        <!--            <i class="fa-solid fa-triangle-exclamation" style="padding-top: 6px"></i> Low Stock!</span>-->
        <!--          </div>-->
        <!--        </template>-->
        <template v-slot:pick="row">
          <Button
              :disabled="row.rowData.isZeroQuantity"
              @click="handlePick(row.rowData)"
              icon="<i class='fa-duotone fa-circle-plus'></i>"
              type="success"
              text="Pick">
          </Button>
        </template>
        <template #menu-slot-items v-if="pickStock.searchData.length > 0">
          <Button
              @click="props.modelValue.openPicksDrawer = !props.modelValue.openPicksDrawer"
              icon="<i class='fa-duotone fa-grid-2'></i>"
              type="toolbar"
              :badgeData="pickStock.pickedItems.length"
              text="View Picks">
          </Button>
          <Button
              @click="handlePick(row.rowData)"
              type="toolbar"
              text="Pick All">
          </Button>
          <!--          <div>Stock Over Pick Available-->
          <!--            <i class="fa-solid fa-circle-check" style="color: #3f9968;"></i>-->
          <!--          </div>-->
        </template>
        <template v-slot:pickQuantity="row">
          <TextInput
              :disable="row.rowData.isZeroQuantity"
              v-model="row.rowData.pickQuantity"
              :width="60"
              :maxLength="8">
          </TextInput>
        </template>
        <template v-slot:comments="row">
          <DropDownTextArea
              v-model="row.rowData.comments"
              :width="310">
          </DropDownTextArea>
        </template>
        <template v-slot:skuProperties="row">
          <GridCellDetail :clickToView="true" v-if="disableGridShowButton(row.grid.skuProperties)">
            <template #details>
              <MoreInfoPopOutPanel
                  v-model="row.grid">
                <template #header>Stock Item Properties</template>
                <template #header-extra>SKU Number:
                  <span style="color: var(--aqua-blue-definition)"> {{ row.grid.skuNumber }}</span></template>
                <template #content>
                  <StockDescriptions v-model="row.grid.skuProperties"></StockDescriptions>
                </template>
              </MoreInfoPopOutPanel>
            </template>
            <template #cell-data>
              <Button
                  v-if="disableGridShowButton(row.grid.skuProperties)"
                  type="toolbar"
                  text="View">
              </Button>
            </template>
          </GridCellDetail>
        </template>
      </StickyGrid>
    </div>
  </StaticWidget>

  <Modal
      v-model="pickStock.openNewConsigneeModal"
      :showClose="false"
      title="New Consignee"
      width="480px"
      height="340px">
    <template #icon><i class="fa-solid fa-circle-plus global-add-icon"></i></template>
    <template #body>
      <div class="cmp-pick-stock_address-entry">
        <AddressEntry
            :countries="pickStock.metaData.countries"
            v-model="address">
        </AddressEntry>
      </div>
    </template>
    <template #footer>
      <Button
          v-if="pickStock.newConsigneeCompany.newCompanySaved"
          @click="editNewCompany"
          icon="<i class='fa-light fa-pen-to-square'></i>"
          text="Edit"
          type="success">
      </Button>
      <Button
          v-if="pickStock.newConsigneeCompany.newCompanySaved"
          @click="clearNewConsigneeForm"
          icon="<i class='fa-light fa-circle-plus'></i>"
          text="Add New"
          type="secondary">
      </Button>
      <Button
          v-if="!pickStock.newConsigneeCompany.newCompanySaved"
          icon="<i class='fa-light fa-floppy-disk'></i>"
          @click="saveNewConsigneeCompany"
          text="Save">
      </Button>
    </template>
  </Modal>
  <Modal
      v-model="pickStock.openEditConsigneeModal"
      :showClose="false"
      title="Edit Consignee"
      width="480px"
      height="340px">
    <template #icon><i class="fa-solid fa-pen-to-square global-edit-icon"></i></template>
    <template #body>
      <div class="cmp-pick-stock_address-entry">
        <AddressEntry
            :countries="pickStock.metaData.countries"
            v-model="address">
        </AddressEntry>
      </div>
    </template>
    <template #footer>
      <Button
          icon="<i class='fa-light fa-floppy-disk'></i>"
          @click="saveEditedConsigneeCompany(address)"
          text="Save">
      </Button>
    </template>
  </Modal>
  <Modal
      v-model="pickStock.openAddContactModal"
      :showClose="false"
      title="Add Contact"
      width="350px"
      height="240px">
    <template #icon><i class="fa-solid fa-circle-plus global-add-icon"></i></template>
    <template #body>
      <div class="cmp-pick-stock_address-entry">
        <ContactEntry
            v-model="contact">
        </ContactEntry>
      </div>
    </template>
    <template #footer>
      <Button
          icon="<i class='fa-light fa-floppy-disk'></i>"
          @click="saveEditedConsigneeCompany(address)"
          text="Save">
      </Button>
    </template>
  </Modal>
  <Modal
      v-model="pickStock.openEditContactModal"
      :showClose="false"
      title="Edit Contact"
      width="350px"
      height="240px">
    <template #icon><i class="fa-solid fa-circle-plus global-add-icon"></i></template>
    <template #body>
      <div class="cmp-pick-stock_address-entry">
        <ContactEntry
            v-model="contact">
        </ContactEntry>
      </div>
    </template>
    <template #footer>
      <Button
          @click="saveEditedConsigneeCompany(address)"
          icon="<i class='fa-light fa-floppy-disk'></i>"
          text="Save"></Button>
    </template>
  </Modal>
</template>

<style lang="scss" scoped>

.cmp-pick-stock {

  @keyframes fadeIn {
    0% {
      //height: 0;
      width: 0;
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  &_address-entry {
    margin-top: 30px;
  }

  &_header-text {
    font-size: 15px;
    //font-weight: bold;
    color: #383838;
  }

  &_delivery-section-layout {
    margin-top: 5px;
    padding: 10px 0 0 20px;
    display: grid;
    gap: 170px;
    grid-template-columns: 460px 40%;
  }

  &_delivery-section-wrapper {
    height: 400px;
  }

  &_address-save-button {
    padding: 5px 0 0 88px;
  }

  &_field-button-layout {
    display: flex;
    gap: 14px;
  }

  &_address-entry-section {
    position: relative;
  }

  &_delivery-information-area {
    padding-left: 70px;
  }

  &_address-details {
    font-size: 0.8em;
    color: var(--aqua-blue-definition);
    padding: 3px;
  }

  &_contact-info {
    margin-top: 2px;
    font-size: 13px;
    padding-left: 3px;
  }

  &_address-placeholder, &_contact-placeholder {
    width: 400px;
    height: 20px;
    margin-top: 5px;
    margin-bottom: 0 !important;
    border-radius: 8px;
    animation: fadeIn ease-in-out 1s;
    background-image: linear-gradient(240deg, #efefef, #fafafa);
  }

  //&_contact-placeholder {
  //  width: 140px;
  //}

  &_contact-icons {
    font-size: 11px;
    color: var(--aqua-blue-definition);
  }

  &_release-date-area {
    display: flex;
    gap: 170px;
    width: 400px;
    height: 50px;
  }

}

</style>
